import * as React from "react";
import { CheckCircle2, Download, Eye, LockKeyhole } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { getDocumentDownload, type ChatSource } from "@/lib/api";

function formatBytes(bytes?: number): string {
  if (!bytes || bytes < 1024) return `${bytes ?? 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function SourceCard({
  source,
  highlighted,
  onPreview,
}: {
  source: ChatSource;
  highlighted: boolean;
  onPreview?: (source: ChatSource) => void;
}) {
  const restricted = source.access_level === "restricted";
  const [downloading, setDownloading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function handleDownload() {
    setDownloading(true);
    setError(null);
    try {
      const data = await getDocumentDownload(source.document_id);
      if ("download_url" in data && data.download_url) {
        const a = document.createElement("a");
        a.href = data.download_url;
        a.download = source.document_name;
        a.target = "_blank";
        a.rel = "noopener";
        a.click();
      } else if ("access_level" in data && data.access_level === "restricted") {
        // Stale-UI safety net — should not normally hit here because the
        // pill below already routes to mailto.
        const subject = encodeURIComponent(
          `Investor Q&A — request access to "${data.document_name}"`,
        );
        window.location.href = `mailto:${data.request_access_email}?subject=${subject}`;
      } else {
        setError("Could not generate download URL.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Download failed.");
    } finally {
      setDownloading(false);
    }
  }

  const requestEmail = source.request_access_email || "ir@greenpointpartners.com";
  const subject = encodeURIComponent(
    `Investor Q&A — request access to "${source.document_name}"`,
  );

  return (
    <Card
      id={`source-${source.index}`}
      data-testid={`source-card-${source.index}`}
      data-highlighted={highlighted ? "true" : "false"}
      className={cn(
        "scroll-mt-4 transition-all duration-200",
        restricted && "border-warning/40 bg-warning-soft/40",
        highlighted && "border-primary ring-2 ring-primary/35",
      )}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <Badge variant={restricted ? "warning" : "success"}>{source.index}</Badge>
          {restricted ? (
            <LockKeyhole className="mt-0.5 size-4 shrink-0 text-warning" />
          ) : (
            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
          )}
        </div>
        <CardTitle className="leading-5">{source.document_name}</CardTitle>
        {source.section ? <CardDescription>{source.section}</CardDescription> : null}
        {source.referenced_in && source.referenced_in.length > 0 ? (
          <div className="mt-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Cited in
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {source.referenced_in.map((sec) => (
                <span
                  key={sec}
                  className="inline-flex max-w-full items-center rounded-md border bg-secondary px-2 py-0.5 text-[11px] font-medium text-secondary-foreground"
                  title={sec}
                >
                  <span className="truncate">{sec}</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="flex items-center justify-between gap-3 text-xs">
        <span className="text-muted-foreground">
          {source.file_type ? source.file_type.toUpperCase() : ""}
          {source.file_size ? ` · ${formatBytes(source.file_size)}` : ""}
        </span>
        {restricted ? (
          <a
            href={`mailto:${requestEmail}?subject=${subject}`}
            className="inline-flex items-center gap-1 rounded-full border border-warning/30 bg-warning-soft px-2.5 py-1 text-xs font-medium text-warning hover:border-warning hover:bg-warning-soft/80"
            title={`Email ${requestEmail} to request access`}
          >
            <LockKeyhole className="size-3" /> Available by request
          </a>
        ) : (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {onPreview ? (
              <Button
                type="button"
                variant="subtle"
                size="sm"
                onClick={() => onPreview(source)}
                aria-label={`Preview ${source.document_name}`}
                title={`Preview ${source.document_name}`}
              >
                <Eye />
                <span>Preview</span>
              </Button>
            ) : null}
            <Button type="button" variant="outline" size="sm" onClick={handleDownload} disabled={downloading}>
              <Download />
              <span>{downloading ? "Opening…" : "Download"}</span>
            </Button>
          </div>
        )}
      </CardContent>
      {error ? (
        <CardContent className="pt-0 text-xs text-destructive">{error}</CardContent>
      ) : null}
    </Card>
  );
}
